import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  AlertTriangle,
  TrendingUp,
  Activity,
  DollarSign,
  CheckCircle2,
  ClipboardCheck,
  GitBranch,
  ShieldAlert,
  Gauge,
  Loader2,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { submitAssessmentLead } from "../lib/api";

const PROBLEMS = [
  {
    icon: AlertTriangle,
    title: "Terraform drift",
    desc: "State mismatches silently accumulating across environments.",
  },
  {
    icon: Activity,
    title: "CI/CD failures",
    desc: "Pipelines breaking in ways nobody's had time to debug properly.",
  },
  {
    icon: DollarSign,
    title: "Rising cloud costs",
    desc: "Azure spend creeping up month over month without clear ownership.",
  },
  {
    icon: TrendingUp,
    title: "Production instability",
    desc: "Flaky deploys, unexpected rollbacks, on-call pages at 3am.",
  },
];

const DELIVERABLES = [
  {
    icon: GitBranch,
    title: "Infrastructure review",
    desc: "Full walkthrough of your Terraform modules, state, and Azure footprint.",
  },
  {
    icon: ClipboardCheck,
    title: "Pipeline audit",
    desc: "End-to-end inspection of your CI/CD — bottlenecks, flake, and security gaps.",
  },
  {
    icon: ShieldAlert,
    title: "Risk analysis",
    desc: "Prioritized list of drift, cost, and reliability risks — ranked by blast radius.",
  },
  {
    icon: Gauge,
    title: "Optimization plan",
    desc: "A concrete 30/60/90-day plan your team can start executing Monday.",
  },
];

export default function Assessment() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const source = params.get("utm_source") || params.get("source") || "direct";
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    challenge: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await submitAssessmentLead({
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        challenge: form.challenge.trim() || null,
        source,
      });
      // Track conversion
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "assessment_request", {
          event_category: "lead",
          event_label: source,
        });
      }
      navigate("/thank-you");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Validation error.");
      setSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document
      .querySelector("#assessment-form")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-[#EDF1FB]">
      <Navbar variant="solid" />

      {/* Hero */}
      <section className="relative overflow-hidden nst-navy text-white pt-[120px] pb-24">
        <div className="absolute inset-0 bg-grid-dark opacity-40" />
        <div
          className="glow-orb w-[520px] h-[520px] -top-40 -left-20"
          style={{ background: "#2E6BF6" }}
        />
        <div
          className="glow-orb w-[460px] h-[460px] top-20 -right-32"
          style={{ background: "#16C47F" }}
        />

        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-3">
            <span className="inline-flex items-center gap-2 pill-label on-dark">
              <Clock className="w-3.5 h-3.5" /> 7-day turnaround · No obligation
            </span>
            <h1
              data-testid="assessment-hero-title"
              className="mt-6 text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight"
            >
              Terraform &amp; CI/CD Audit for <span className="text-[#6B9FFF]">Azure Teams</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/80 leading-relaxed max-w-2xl">
              Identify pipeline failures, Terraform drift, and hidden cloud risks in <strong className="text-white">7 days</strong>. Real senior architects. Actionable output. Zero fluff.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button
                data-testid="assessment-hero-cta"
                onClick={scrollToForm}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-[#2E6BF6] hover:bg-[#1E55D6] text-white font-semibold transition-colors text-base shadow-lg shadow-blue-900/20"
              >
                Request Free Assessment <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#what-you-get"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl border border-white/20 hover:bg-white/5 text-white font-semibold transition-colors text-base"
              >
                See what's included
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 text-white/70 max-w-xl">
              <div>
                <div className="text-2xl font-extrabold text-white">7 days</div>
                <div className="text-xs uppercase tracking-wider">turnaround</div>
              </div>
              <div>
                <div className="text-2xl font-extrabold text-white">Senior</div>
                <div className="text-xs uppercase tracking-wider">architects only</div>
              </div>
              <div>
                <div className="text-2xl font-extrabold text-white">$0</div>
                <div className="text-xs uppercase tracking-wider">obligation</div>
              </div>
            </div>
          </div>

          {/* Form card */}
          <div className="lg:col-span-2">
            <div
              id="assessment-form"
              className="bg-white/[0.05] border border-white/10 rounded-2xl p-7 backdrop-blur-sm"
            >
              <h3 className="text-xl font-bold">Request Assessment</h3>
              <p className="mt-1 text-sm text-white/65">
                We'll review and respond within one business day.
              </p>
              <form onSubmit={handleSubmit} className="mt-5 space-y-4">
                <input
                  data-testid="assessment-form-name"
                  type="text"
                  placeholder="Full name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-[#2E6BF6]"
                  required
                />
                <input
                  data-testid="assessment-form-email"
                  type="email"
                  placeholder="Work email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-[#2E6BF6]"
                  required
                />
                <input
                  data-testid="assessment-form-company"
                  type="text"
                  placeholder="Company"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-[#2E6BF6]"
                />
                <textarea
                  data-testid="assessment-form-challenge"
                  rows={4}
                  placeholder="Your biggest platform challenge right now…"
                  value={form.challenge}
                  onChange={(e) =>
                    setForm({ ...form, challenge: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-[#2E6BF6] resize-none"
                />
                <button
                  data-testid="assessment-form-submit"
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex justify-center items-center gap-2 py-3.5 rounded-xl bg-[#2E6BF6] hover:bg-[#1E55D6] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold transition-colors"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>
                      Get My Assessment <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
                <p className="text-xs text-white/50 text-center">
                  No spam. No upsells. One architect, one review.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Problems */}
      <section className="py-24 lg:py-28">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <span className="pill-label">Signals</span>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0A1F47] leading-tight">
              Your platform may have hidden issues
            </h2>
            <p className="mt-4 text-base md:text-lg text-[#0A1F47]/70">
              If any of these feel familiar, a short audit usually pays for itself within the first sprint.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROBLEMS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  data-testid={`problem-card-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-white rounded-2xl p-6 border border-[#E2E8F4] hover:border-[#2E6BF6] hover:shadow-md transition-all"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#FFEFE6] text-[#F06A3E] flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-[#0A1F47]">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm text-[#0A1F47]/70 leading-relaxed">
                    {p.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section
        id="what-you-get"
        className="py-24 lg:py-28 bg-white border-y border-[#E2E8F4]"
      >
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <span className="pill-label">Deliverables</span>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0A1F47] leading-tight">
              What you get in 7 days
            </h2>
            <p className="mt-4 text-base md:text-lg text-[#0A1F47]/70">
              Concrete, opinionated, written output — not a slide deck.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {DELIVERABLES.map((d) => {
              const Icon = d.icon;
              return (
                <div
                  key={d.title}
                  data-testid={`deliverable-card-${d.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex items-start gap-5 bg-[#F5F7FA] rounded-2xl p-6 md:p-8 border border-[#E2E8F4]"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#0A1F47] text-white flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-[#0A1F47]">
                      {d.title}
                    </h3>
                    <p className="mt-2 text-sm md:text-base text-[#0A1F47]/70 leading-relaxed">
                      {d.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-14 text-center">
            <button
              data-testid="deliverables-cta"
              onClick={scrollToForm}
              className="btn-navy inline-flex"
            >
              Request Free Assessment <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Social proof / how */}
      <section className="py-24 lg:py-28">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="rounded-3xl bg-[#0A1F47] text-white overflow-hidden relative p-10 md:p-16 lg:p-20 border border-[#0A1F47]">
            <div className="absolute inset-0 bg-grid-dark opacity-20" />
            <div
              className="glow-orb w-[400px] h-[400px] -top-20 -right-10"
              style={{ background: "#16C47F" }}
            />
            <div className="relative max-w-3xl">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight">
                Built by engineers who've owned Azure platforms at scale
              </h2>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#16C47F] mt-0.5" />
                  <div>
                    <div className="text-white font-semibold">Senior only</div>
                    <div className="text-sm text-white/65">
                      Never outsourced, never juniors.
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#16C47F] mt-0.5" />
                  <div>
                    <div className="text-white font-semibold">Hands-on</div>
                    <div className="text-sm text-white/65">
                      We read the code. Every line.
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#16C47F] mt-0.5" />
                  <div>
                    <div className="text-white font-semibold">Actionable</div>
                    <div className="text-sm text-white/65">
                      Your team ships fixes from day 8.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
